import { clsx } from "clsx";
import { Frequency, RRule } from "rrule";
import { twMerge } from "tailwind-merge";
import { BookingService } from "../services/BookingService";
import { LocationService } from "../services/LocationService";
import { MemberService } from "../services/MemberService";
import { OrganisationService } from "../services/OrganisationService";
import { RoomService } from "../services/RoomService";
import type { Recurrence } from "../types/Recurrence";
import { sameUtcDay } from "@/lib/calendarDay";
import type { Member } from "@/types/Member";
import type { ClassValue } from "clsx";
import type { Options } from "rrule";

interface MergeClassesParams {
  inlineArray: string[];
  propArray?: string[];
}

export const mergeClasses = (
  inlineArray: MergeClassesParams["inlineArray"],
  propArray?: MergeClassesParams["propArray"],
): string[] => {
  if (!propArray || propArray.length === 0) return inlineArray;
  if (!Array.isArray(propArray)) {
    console.warn("Passing a non-array to mergeClasses");
    return inlineArray;
  }
  return inlineArray.concat(propArray.filter((item) => inlineArray.indexOf(item) < 0));
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a date/instant in the VIEWER's LOCAL timezone. Use this for genuine time-of-day
 * instants (createdAt, updatedAt, lastLoginAt, syncedAt, acceptedAt, etc.) where the correct
 * calendar day depends on the viewer's own timezone — a UK-created record at 00:30 BST should
 * read as today for a UK viewer even though a viewer elsewhere might see a different local day.
 *
 * For DAY-ONLY values that are stored as UTC-midnight of a specific calendar day (a booking
 * `date`, a membership end date, a recurrence `until`/exception) use `formatUtcDate` instead —
 * those values must render the SAME day for every viewer regardless of timezone.
 */
export function formatDate(date: Date | string | undefined, withDayName = false, shortMonthName = false): string {
  if (!date) {
    return "";
  }

  // Convert string to Date if needed
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return "";
  }

  return dateObj.toLocaleDateString("en-GB", {
    weekday: withDayName ? "long" : undefined,
    day: "2-digit",
    month: shortMonthName ? "short" : "long",
    year: "numeric",
  });
}

/**
 * Formats a DAY-ONLY value (stored as UTC-midnight of a specific calendar day — a booking
 * `date`, a membership end date, a recurrence `until`/exception) in UTC, so every viewer sees
 * the SAME day regardless of their own timezone. Mirrors `formatDate`'s output format/options;
 * use `formatDate` instead for genuine time-of-day instants (createdAt, lastLoginAt, syncedAt…).
 */
export function formatUtcDate(date: Date | string | undefined, withDayName = false, shortMonthName = false): string {
  if (!date) {
    return "";
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return "";
  }

  return dateObj.toLocaleDateString("en-GB", {
    timeZone: "UTC",
    weekday: withDayName ? "long" : undefined,
    day: "2-digit",
    month: shortMonthName ? "short" : "long",
    year: "numeric",
  });
}

export function isValidDate(date: Date | undefined) {
  if (!date) {
    return false;
  }
  return !isNaN(date.getTime());
}

/**
 * Formats a date with time in a verbose format: "Monday 16th February 2026 at 20:44"
 */
export function formatDateTimeVerbose(date: Date | string | undefined): string {
  if (!date) {
    return "";
  }

  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return "";
  }

  const dayName = dateObj.toLocaleDateString("en-GB", { weekday: "long" });
  const day = dateObj.getDate();
  const month = dateObj.toLocaleDateString("en-GB", { month: "long" });
  const year = dateObj.getFullYear();
  const time = dateObj.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });

  // Add ordinal suffix to day
  const ordinal = (n: number): string => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  return `${dayName} ${ordinal(day)} ${month} ${year} at ${time}`;
}

/**
 * Returns a humanized duration string between two times (e.g. "1hr 30m").
 * Assumes startTime and endTime are in "HHmm" format.
 */
export function humanizeDuration(startTime: string, endTime: string): string {
  const startHour = parseInt(startTime.slice(0, 2), 10);
  const startMinute = parseInt(startTime.slice(2, 4), 10);
  const endHour = parseInt(endTime.slice(0, 2), 10);
  const endMinute = parseInt(endTime.slice(2, 4), 10);

  const start = new Date(0, 0, 0, startHour, startMinute);
  const end = new Date(0, 0, 0, endHour, endMinute);

  let diff = (end.getTime() - start.getTime()) / 1000; // seconds
  if (diff < 0) diff += 24 * 3600; // handle overnight bookings

  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);

  let result = "";
  if (hours > 0) result += `${hours}hr `;
  if (minutes > 0) result += `${minutes}m`;
  return result.trim() || "0m";
}

export const getCookie = (key: string): string | null => {
  if (!key) {
    return null;
  }
  return (
    decodeURIComponent(
      document.cookie.replace(
        new RegExp(
          "(?:(?:^|.*;)\\s*" + encodeURIComponent(key).replace(/[-.+*]/g, "\\$&") + "\\s*\\=\\s*([^;]*).*$)|^.*$",
        ),
        "$1",
      ),
    ) || null
  );
};

export const setCookie = (
  key: string,
  value: string,
  end?: number | string | Date,
  path?: string,
  domain?: string,
  secure?: boolean,
  maxAge?: number,
): boolean => {
  if (!key || /^(?:expires|max-age|path|domain|secure)$/i.test(key)) {
    return false;
  }
  let expires = "";
  if (end) {
    switch (end.constructor) {
      case Number:
        expires =
          end === Infinity
            ? "; expires=Fri, 31 Dec 9999 23:59:59 GMT"
            : "; expires=" + new Date((end as number) * 1e3 + Date.now()).toUTCString();
        break;
      case String:
        expires = "; expires=" + end;
        break;
      case Date:
        expires = "; expires=" + (end as Date).toUTCString();
        break;
      default:
        expires = "";
    }
  }
  document.cookie =
    encodeURIComponent(key) +
    "=" +
    encodeURIComponent(value) +
    expires +
    (domain ? "; domain=" + domain : "") +
    (maxAge ? "; max-age=" + maxAge : "") +
    (path ? "; path=" + path : "") +
    (secure ? "; secure" : "");
  return true;
};

export const removeCookie = (key: string, path?: string, domain?: string): boolean => {
  if (!hasCookie(key)) {
    return false;
  }
  document.cookie =
    encodeURIComponent(key) +
    "=; expires=Thu, 01 Jan 1970 00:00:00 GMT" +
    (domain ? "; domain=" + domain : "") +
    (path ? "; path=" + path : "");
  return true;
};

export const hasCookie = (key: string): boolean => {
  if (!key || /^(?:expires|max-age|path|domain|secure)$/i.test(key)) {
    return false;
  }
  return new RegExp("(?:^|;\\s*)" + encodeURIComponent(key).replace(/[-.+*]/g, "\\$&") + "\\s*\\=").test(
    document.cookie,
  );
};

export const humaniseDate = (date: Date) => {
  if (!date) return date;
  const today = new Date();
  const tomorrow = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + 1));
  const yesterday = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - 1));

  if (sameUtcDay(date, today)) {
    return "Today";
  } else if (sameUtcDay(date, tomorrow)) {
    return "Tomorrow";
  } else if (sameUtcDay(date, yesterday)) {
    return "Yesterday";
  }

  return date.toLocaleDateString("en-GB", { timeZone: "UTC" });
};

/**
 * Friendlier date label used by the "Your bookings" table (and similar lists). Today / Tomorrow
 * / Yesterday short-circuit as before; otherwise the date is prefixed with a UK-style short
 * weekday name (Mon, Tues, Wed, Thurs, Fri, Sat, Sun) and renders as "Thurs, 30th April". The
 * year is only appended when the date isn't in the current year ("Thurs, 30th April 2027").
 */
const SHORT_WEEKDAY = ["Sun", "Mon", "Tues", "Wed", "Thurs", "Fri", "Sat"];

export const humaniseDateFriendly = (date: Date, today: Date = new Date()): string => {
  if (!date) return "";
  const tomorrow = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + 1));
  const yesterday = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - 1));

  if (sameUtcDay(date, today)) return "Today";
  if (sameUtcDay(date, tomorrow)) return "Tomorrow";
  if (sameUtcDay(date, yesterday)) return "Yesterday";

  const day = date.getUTCDate();
  const monthName = date.toLocaleDateString("en-GB", { month: "long", timeZone: "UTC" });
  const ordinal = (n: number): string => {
    if (n >= 11 && n <= 13) return "th";
    switch (n % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  const weekday = SHORT_WEEKDAY[date.getUTCDay()];
  const yearSuffix = date.getUTCFullYear() === today.getUTCFullYear() ? "" : ` ${date.getUTCFullYear()}`;
  return `${weekday}, ${day}${ordinal(day)} ${monthName}${yearSuffix}`;
};

export function getInitials(str: string): string {
  return str
    .split(" ")
    .map((word) => word[0]?.toUpperCase() || "")
    .join("");
}

/**
 * Short label for an amenity calendar column. Like getInitials, but KEEPS whole numbers so numbered
 * amenities stay distinguishable in the narrow header — e.g. "Car Parking Space 14/15/16" become
 * "CPS14/CPS15/CPS16" instead of the ambiguous "CPS1" for all three (getInitials took only the "1"
 * from "14"). Splits on spaces and hyphens; alphabetic parts contribute their initial, numeric parts
 * are kept in full. "Massage Couch" → "MC", "CP-14" → "C14".
 */
export function getAmenityColumnLabel(name: string): string {
  return name
    .trim()
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((word) => (/^\d+$/.test(word) ? word : word[0]?.toUpperCase() || ""))
    .join("");
}

/**
 * Returns the display label for a location.
 * Uses the custom label if set, otherwise falls back to "${city} ${name}".
 */
export function getLocationLabel(location: { label?: string; city?: string; name?: string }): string {
  if (location.label) return location.label;
  return `${location.city || ""} ${location.name || ""}`.trim();
}

/**
 * Orders locations for the location pickers: a member's preferred locations first (in the
 * order they set them), then everything else alphabetically by label. Returns a new array;
 * the input is not mutated. `preferredIds` are location ids (matching Location.id).
 */
export function sortLocationsByPreference<T extends { id: string; label?: string; city?: string; name?: string }>(
  locations: T[],
  preferredIds: string[] | undefined | null,
): T[] {
  const preferred = preferredIds ?? [];
  const rank = (id: string) => {
    const i = preferred.indexOf(id);
    return i === -1 ? Number.POSITIVE_INFINITY : i;
  };
  return [...locations].sort((a, b) => {
    const ra = rank(a.id);
    const rb = rank(b.id);
    if (ra !== rb) return ra - rb;
    return getLocationLabel(a).localeCompare(getLocationLabel(b));
  });
}

// Returns true if the given slug matches the current page slug
export function isCurrentSlug(slug: string, options?: { exact?: boolean }): boolean {
  if (typeof window === "undefined") return false;
  const currentPath = window.location.pathname.replace(/\/$/, "");
  const compareSlug = slug.replace(/\/$/, "");
  if (options?.exact) {
    return currentPath === compareSlug;
  }
  // Only match if currentPath starts with compareSlug + "/" (not just compareSlug)
  return currentPath === compareSlug || currentPath.startsWith(compareSlug + "/");
}

export function shorthandFloorName(floor: string): string {
  switch (floor.toLowerCase()) {
    case "ground":
      return "G";
    case "first":
      return "1st";
    case "second":
      return "2nd";
    case "third":
      return "3rd";
    case "basement":
      return "B";
    default:
      return floor;
  }
}

/**
 * Returns a compact badge label for a floor name.
 * Standard floors return single characters (B, G, 1, 2, 3).
 * Named floors return initials (e.g., "Old Garage" → "OG").
 */
export function getFloorBadgeLabel(floor: string): string {
  if (!floor) return "";

  const normalized = floor.toLowerCase().trim();

  if (normalized.includes("basement") || normalized.includes("lower ground")) return "B";
  if (normalized === "ground" || normalized.includes("ground floor")) return "G";
  if (normalized === "first" || normalized.includes("first floor")) return "1";
  if (normalized === "second" || normalized.includes("second floor")) return "2";
  if (normalized === "third" || normalized.includes("third floor")) return "3";

  // For named floors, return initials (e.g., "Old Garage" → "OG")
  return floor
    .split(/\s+/)
    .map((word) => word[0]?.toUpperCase() || "")
    .join("");
}

/**
 * Floor ordering map for sorting rooms by floor level.
 * Lower numbers come first (basement/cellar at bottom, then ground, then upper floors).
 */
const floorOrder: Record<string, number> = {
  basement: 0,
  cellar: 0,
  ground: 1,
  mezzanine: 1.5,
  first: 2,
  second: 3,
  third: 4,
  fourth: 5,
  fifth: 6,
};

/**
 * Gets the numeric order value for a floor name.
 * Unknown floors are placed at the end (99).
 */
export function getFloorOrder(floor: string | undefined | null): number {
  if (!floor) return 99;
  return floorOrder[floor.toLowerCase()] ?? 99;
}

/**
 * Sorts an array of rooms by floor order (ground floor first, then ascending).
 * Rooms with unknown or missing floor values are placed at the end.
 */
export function sortRoomsByFloor<T extends { floor?: string | null }>(rooms: T[]): T[] {
  return [...rooms].sort((a, b) => getFloorOrder(a.floor) - getFloorOrder(b.floor));
}

/**
 * Sorts an array of rooms by displayOrder (ascending).
 * Rooms with no displayOrder are placed at the end, sorted alphabetically.
 */
export function sortRoomsByDisplayOrder<T extends { displayOrder?: number | null; name: string }>(rooms: T[]): T[] {
  return [...rooms].sort((a, b) => {
    const orderA = a.displayOrder ?? 999999;
    const orderB = b.displayOrder ?? 999999;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Converts a GUID to a 6-character uppercase alphanumeric reference using
 * Base36 encoding. Uppercase-only and therefore case-insensitive to read aloud
 * or type — there is no lowercase variant a user could get "wrong".
 *
 * Mirror of ember/src/utils/guidToShortCode.ts — keep the two in sync.
 *
 * @param guid - The GUID to convert (e.g., "550e8400-e29b-41d4-a716-446655440000")
 * @returns A 6-character uppercase string (e.g., "AB3XK9")
 */
export function guidToShortCode(guid: string): string {
  // Remove hyphens and take first 16 hex characters (64 bits)
  const hex = guid.replace(/-/g, "").substring(0, 16);

  // Convert hex to decimal
  const decimal = BigInt("0x" + hex);

  // Base36 alphabet (digits + uppercase letters only)
  const base36 = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";

  let result = "";
  let num = decimal;

  // Convert to base36
  while (num > 0n && result.length < 6) {
    result = base36[Number(num % 36n)] + result;
    num = num / 36n;
  }

  // Pad with zeros if needed
  return result.padStart(6, "0");
}

/**
 * Safely parses a date string or Date object to a Date, or returns undefined if invalid.
 * Accepts ISO strings, timestamps, or Date objects. Returns undefined for invalid input.
 */
export function parseDate(date: string | Date | undefined | null): Date | undefined {
  if (!date) return undefined;
  if (date instanceof Date && !isNaN(date.getTime())) return date;
  if (typeof date === "string") {
    const parsed = new Date(date);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  return undefined;
}

export function castToRRule(recurrence: Recurrence): RRule {
  // Map Recurrence.frequency to RRule frequency
  const freqMap: Record<string, Frequency> = {
    daily: Frequency.DAILY,
    weekly: Frequency.WEEKLY,
    monthly: Frequency.MONTHLY,
  };

  const ruleOptions: Partial<Options> = {
    freq: freqMap[recurrence.frequency] ?? Frequency.WEEKLY,
    interval: recurrence.interval ?? 1,
    count: recurrence.count,
    until:
      recurrence.until instanceof Date ? recurrence.until : recurrence.until ? new Date(recurrence.until) : undefined,
    byweekday: recurrence.byWeekday?.map((day) => (RRule as any)[day]) ?? undefined,
  };

  return new RRule(ruleOptions as Options);
}

export async function clearAllApiCaches() {
  // Import services dynamically to avoid circular deps
  const bookingService = new BookingService();
  const locationService = new LocationService();
  const memberService = new MemberService();
  const roomService = new RoomService();
  const organisationService = new OrganisationService();
  bookingService.api.clearCache();
  locationService.api.clearCache();
  memberService.api.clearCache();
  roomService.api.clearCache();
  organisationService.api.clearCache();
  // Add more as needed
}

export function parseCustomDate(dateStr: string): Date {
  // If already ISO, just return new Date(dateStr)
  if (/^\d{4}-\d{2}-\d{2}T/.test(dateStr)) return new Date(dateStr);

  // Convert DD-MM-YYYY to YYYY-MM-DD
  const match = /^(\d{2})-(\d{2})-(\d{4})T(.*)$/.exec(dateStr);
  if (match) {
    const [, dd, mm, yyyy, time] = match;
    return new Date(`${yyyy}-${mm}-${dd}T${time}`);
  }
  // Fallback: try Date constructor
  return new Date(dateStr);
}

/**
 * Formats a date string to display as "Month Year" (e.g., "April 2024").
 * Handles custom date format (DD-MM-YYYY) and standard ISO formats.
 *
 * Renders the month/year of the given instant in UTC, not the viewer's browser-local timezone —
 * callers use this for day/month-identity labels (e.g. "Member since December 2025"), so a
 * viewer east of UTC must not see the label roll over to the following month/year early.
 *
 * @param dateString - The date string to format (e.g., "26-04-2024T10:00:00Z")
 * @returns Formatted string in "Month Year" format (e.g., "April 2024")
 */
export function formatMonthYear(dateString: string): string {
  const date = parseCustomDate(dateString);

  return new Intl.DateTimeFormat("en-GB", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export function amILookingAtMyself(loggedInMember: Member, viewedMember: Member): boolean {
  return loggedInMember.id === viewedMember.id;
}

/**
 * Formats a total number of minutes as "H:MM" (e.g. 90 → "1:30"). Negative input clamps to "0:00".
 */
export function formatMinutesAsHMM(totalMinutes: number): string {
  const safe = Math.max(0, Math.round(totalMinutes));
  const hrs = Math.floor(safe / 60);
  const mins = safe % 60;
  return `${hrs}:${mins.toString().padStart(2, "0")}`;
}

/**
 * Formats a date as "<day><ordinal> <Month>" in en-GB (e.g. "7th May"). Returns "" for invalid input.
 * When passing a string, supply a full ISO timestamp with a time component (e.g. "2026-05-07T00:00:00.000Z"),
 * not a bare "YYYY-MM-DD" — bare date strings are parsed as UTC midnight and can render the previous day
 * in timezones behind UTC. The billing API always returns full ISO timestamps.
 */
export function formatOrdinalDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  const day = d.getDate();
  const month = d.toLocaleDateString("en-GB", { month: "long" });
  const ordinal = (n: number): string => {
    if (n >= 11 && n <= 13) return "th";
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };
  return `${day}${ordinal(day)} ${month}`;
}
